import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import GlassCard from "../../../components/common/GlassCard";

function getStatusColor(status) {
  switch (status) {
    case "active":
      return "error";
    case "chronic":
      return "warning";
    case "resolved":
      return "success";
    default:
      return "default";
  }
}

function ProblemsCard({ problems, onAddProblem, onOpenLookup, onDeleteProblem }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeProblemId, setActiveProblemId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveProblemId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveProblemId(null);
  };

  return (
    <GlassCard
      title="Active Problems"
      icon={<MedicalServicesIcon color="error" />}
      onAdd={onAddProblem}
      addLabel="Add Problem"
      onLookup={onOpenLookup}
      lookupLabel="ICD-10"
    >
      {problems?.length ? (
        problems.map((problem) => (
          <Box
            key={problem.id}
            mb={1}
            sx={{
              p: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
            data-testid={`problem-item-${problem.id}`}
            data-problem-code={problem.code}
            data-problem-status={problem.status}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box flex={1}>
                <Typography variant="body2" fontWeight={500}>
                  {problem.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {problem.code} • Onset: {problem.onset_date || "Unknown"}
                </Typography>
              </Box>
              <Box display="flex" gap={0.5} alignItems="center">
                <Chip 
                  label={problem.status} 
                  size="small" 
                  color={getStatusColor(problem.status)} 
                  variant="outlined"
                  data-testid={`problem-status-chip-${problem.id}`}
                  aria-label={`Problem status: ${problem.status}`}
                />
                <Tooltip title="Options">
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleMenuOpen(e, problem.id)}
                      data-testid={`problem-menu-button-${problem.id}`}
                      aria-label={"Problem options"}
                      data-action="problem-menu"
                      data-problem-id={problem.id}
                    >
                      <MoreVertIcon 
                        aria-label={"Problem options"} 
                        fontSize="small"
                        data-testid={`problem-options-icon-${problem.id}`}
                        data-action="problem-menu"
                        data-category="problems"
                      />
                    </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No active problems
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (activeProblemId) {
              onDeleteProblem(activeProblemId);
            }
          }}
          data-testid={`problem-delete-menu-item-${activeProblemId}`}
          aria-label="Delete problem"
          data-action="delete-problem"
          data-problem-id={activeProblemId}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

ProblemsCard.propTypes = {
  problems: PropTypes.array,
  onAddProblem: PropTypes.func.isRequired,
  onOpenLookup: PropTypes.func.isRequired,
  onDeleteProblem: PropTypes.func.isRequired,
};

ProblemsCard.defaultProps = {
  problems: [],
};

export default ProblemsCard;
